import { GetServerSideProps } from 'next';
import ErrorPage from 'next/error';
import Link from 'next/link';
import { allSettled, fork, serialize } from 'effector';
import { useStoreMap, useUnit } from 'effector-react';
import { RiArrowRightLine, RiBarChartHorizontalLine, RiHeartFill, RiHeartLine, RiPhoneLine } from 'react-icons/ri';

import { MainLayout } from '@/layouts/main-layout';

import { getCartUnitPrice } from '@/features/cart/cart-data';
import { $cartList, pushToCartList } from '@/features/cart/model';
import { $comparisonList, deleteProductFromCompare, pushToComparisonList } from '@/features/compare/model';
import { $favoriteList, deleteProductFromFavorites, pushhToFavoriteList } from '@/features/favorites/model';

import { productByIdQuery } from '@/entities/product/model';

import type { Product } from '@/shared/api/generated/api.generated';
import { Breadcrumbs } from '@/shared/components/breadcrumbs';
import { DiscontinuedProductPanel, ProductAlternatives } from '@/shared/components/precision/discontinued-product';
import { ProductGallery } from '@/shared/components/precision/product-gallery';
import { pagesPath } from '@/shared/config/$path';
import { formatCurrency } from '@/shared/lib/currency';
import { EFFECTOR_STATE_KEY } from '@/shared/lib/effector';
import { getCategoryBreadcrumbs } from '@/shared/lib/get-category-breadcrumbs';
import { removeTags } from '@/shared/lib/remove-tags';
import { sanitizeProductDescription } from '@/shared/lib/sanitize-product-description';
import { PageSeo } from '@/shared/lib/seo';

import { productPage } from './index.model';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const scope = fork();
  const id = context.params?.id as string;
  await allSettled(productPage.open, { scope, params: { query: { id } } });
  if (!scope.getState(productByIdQuery.$data)) return { notFound: true };
  return { props: { [EFFECTOR_STATE_KEY]: serialize(scope) } };
};
export interface ProductPageProps {
  previewProduct?: Product;
  discontinued?: boolean;
  alternatives?: Product[];
  preview?: boolean;
}

export default function ProductPage({
  previewProduct,
  discontinued = false,
  alternatives = [],
  preview = false,
}: ProductPageProps = {}) {
  const loadedProduct = useUnit(productByIdQuery.$data);
  const product = previewProduct ?? loadedProduct;
  const [add, addCompare, addFavorite, removeFavorite, removeCompare] = useUnit([
    pushToCartList,
    pushToComparisonList,
    pushhToFavoriteList,
    deleteProductFromFavorites,
    deleteProductFromCompare,
  ]);
  const inCart = useStoreMap({
    store: $cartList,
    keys: [product?.id],
    fn: (list, [id]) => list.some((item) => item.product.id === id),
  });
  const favorite = useStoreMap({
    store: $favoriteList,
    keys: [product?.id],
    fn: (list, [id]) => list.some((item) => item.id === id),
  });
  const comparison = useStoreMap({
    store: $comparisonList,
    keys: [product?.id],
    fn: (list, [id]) => list.some((item) => item.id === id),
  });
  if (!product) return <ErrorPage statusCode={404} />;
  const price = getCartUnitPrice(product);
  const attributes = Object.entries(product.attributes || {});
  return (
    <>
      <PageSeo
        title={product.name}
        description={removeTags(product.description)}
        noindex={preview}
        nofollow={preview}
      />
      <MainLayout>
        <div className={`product-detail container${discontinued ? ' product-detail--discontinued' : ''}`}>
          {preview && (
            <div className="discontinued-preview-note">
              <span>ПРИМЕР СТРАНИЦЫ</span>
              Статус модели и подбор аналогов показаны для демонстрации.
            </div>
          )}
          <Breadcrumbs
            paths={[
              ['Каталог', pagesPath.catalog.$url()],
              ...getCategoryBreadcrumbs({ category: product.category }),
              [product.name],
            ]}
          />
          <div className="product-detail__heading">
            <p className="eyebrow">
              {product.brand || 'ПРОМЫШЛЕННОЕ ОБОРУДОВАНИЕ'}
              {product.article && ` / АРТ. ${product.article}`}
            </p>
            <h1>{product.name}</h1>
            {discontinued && (
              <p className="discontinued-heading-note">
                <span className="discontinued-heading-status">Снят с производства</span>
                <span>Характеристики сохранены</span>
              </p>
            )}
          </div>
          <div className="product-detail__grid">
            <ProductGallery images={product.media.images} name={product.name} />
            <aside className={`purchase-panel${discontinued ? ' purchase-panel--discontinued' : ''}`}>
              {discontinued ? (
                <DiscontinuedProductPanel productName={product.name} count={alternatives.length} />
              ) : (
                <>
                  <span className="purchase-stock stock-dot">
                    {product.stock > 0 ? `В наличии: ${product.stock} шт.` : 'Наличие уточнит менеджер'}
                  </span>
                  <p className="purchase-label">Стоимость оборудования</p>
                  <div className="purchase-price">{price === undefined ? 'По запросу' : formatCurrency(price)}</div>
                  <p className="purchase-note">Актуальную стоимость и комплектацию подтвердит менеджер.</p>
                  {inCart ? (
                    <Link className="precision-button" href="/cart">
                      Перейти в корзину
                      <RiArrowRightLine />
                    </Link>
                  ) : (
                    <button className="precision-button" type="button" onClick={() => add(product)}>
                      Добавить в корзину
                      <RiArrowRightLine />
                    </button>
                  )}
                  <div className="purchase-secondary">
                    <button
                      type="button"
                      aria-pressed={favorite}
                      onClick={() => (favorite ? removeFavorite(product.id) : addFavorite(product))}
                    >
                      {favorite ? <RiHeartFill /> : <RiHeartLine />}
                      {favorite ? 'В избранном' : 'В избранное'}
                    </button>
                    <button
                      type="button"
                      aria-pressed={comparison}
                      onClick={() => (comparison ? removeCompare(product.id) : addCompare(product))}
                    >
                      <RiBarChartHorizontalLine />
                      {comparison ? 'В сравнении' : 'Сравнить'}
                    </button>
                  </div>
                  <div className="purchase-help">
                    <p>Нужна помощь с выбором?</p>
                    <a href="tel:+73433630507">
                      <RiPhoneLine />
                      +7 (343) 363-05-07
                    </a>
                    <a href="mailto:zakaz@domstankov.com">zakaz@domstankov.com</a>
                  </div>
                  <div className="purchase-delivery">
                    <span>Доставка по России</span>
                    <p>Сроки и стоимость согласуем с учётом вашего региона.</p>
                    <Link className="text-link" href="/delivery-and-payment">
                      Условия доставки
                      <RiArrowRightLine />
                    </Link>
                  </div>
                </>
              )}
            </aside>
          </div>
          {discontinued && <ProductAlternatives products={alternatives} productName={product.name} />}
          <nav className="product-detail__nav" aria-label="О товаре">
            <a href="#specifications">
              Характеристики<span>{attributes.length}</span>
            </a>
            <a href="#description">Описание</a>
            <Link href="/delivery-and-payment">Доставка и оплата</Link>
          </nav>
          <div className="product-description-grid">
            <section id="specifications">
              <p className="eyebrow">В ДЕТАЛЯХ</p>
              <h2>Технические характеристики</h2>
              {attributes.length ? (
                <dl className="specifications">
                  {attributes.map(([key, value]) => (
                    <div key={key}>
                      <dt>{key}</dt>
                      <dd>{String(value)}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p>Уточните характеристики у нашего специалиста.</p>
              )}
            </section>
            <section id="description">
              <p className="eyebrow">ОБ ОБОРУДОВАНИИ</p>
              <h2>{discontinued ? 'Об этой модели' : 'Создано для вашей задачи.'}</h2>
              {product.description ? (
                <div
                  className="product-rich-text"
                  dangerouslySetInnerHTML={{ __html: sanitizeProductDescription(product.description) }}
                />
              ) : (
                <p>Расскажем об особенностях оборудования и поможем подобрать комплектацию под ваше производство.</p>
              )}
              <a className="text-link" href="mailto:zakaz@domstankov.com">
                Задать вопрос специалисту
                <RiArrowRightLine />
              </a>
            </section>
          </div>
          <p className="product-disclaimer">
            {discontinued
              ? 'Характеристики архивной модели приведены для справки. Комплектацию и параметры замены подтвердит специалист.'
              : 'Стоимость может быть изменена. Информация о товаре не является публичной офертой.'}{' '}
            <Link href="/offer">Подробнее об условиях</Link>.
          </p>
        </div>
      </MainLayout>
    </>
  );
}
